/**
 * Document upload with AI extraction.
 *
 * This component uploads PDFs to the extraction API, shows progress,
 * and displays extracted data for review.
 */

import { useCallback } from "react";
import { trackEvent } from "@/lib/analytics";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  FileText,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Eye,
  X,
  RotateCcw,
} from "lucide-react";
import { useTenant } from "@/hooks/useTenant";
import { supabase } from "@/integrations/supabase/client";
import { useDocumentExtraction, ExtractionJob } from "@/hooks/useDocumentExtraction";
import { DocumentQueue } from "@/components/data/DocumentQueue";
import { BatchUploadZone } from "@/components/data/BatchUploadZone";

// Map document types to friendly names
const documentTypeLabels: Record<string, string> = {
  capital_call: "Capital Call",
  distribution_notice: "Distribution Notice",
  quarterly_account_statement: "Quarterly Statement",
  quarterly_financial_statement: "Financial Statement",
  portfolio_statement_custodian: "Portfolio Statement (Custodian)",
  portfolio_statement_consolidated: "Portfolio Statement (Consolidated)",
  unknown: "Unknown",
};

// Map status to badge variant
const statusVariants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  uploaded: "secondary",
  processing: "default",
  completed: "default",
  failed: "destructive",
};

// Test client UUID for development when user has no org
const TEST_CLIENT_ID = "f7b74039-2131-49d2-9540-463f6ed5d386";

export function ExtractionUploadTab() {
  const { orgId, familyId } = useTenant();
  const navigate = useNavigate();

  // Fetch the client record for the user's org
  // client_id must reference the clients table, not orgs table
  const { data: clientData } = useQuery({
    queryKey: ['client-for-org', orgId],
    queryFn: async () => {
      if (!orgId) return null;
      const { data, error } = await supabase
        .from('clients')
        .select('id')
        .eq('org_id', orgId)
        .limit(1)
        .single();
      if (error) {
        console.warn('No client found for org:', orgId, error);
        return null;
      }
      return data;
    },
    enabled: !!orgId,
  });

  // Use the fetched client_id, or fall back to test client
  const clientId = clientData?.id || TEST_CLIENT_ID;

  const {
    activeJobs,
    isUploading,
    isExtracting,
    uploadAndExtract,
    uploadBatch,
    batchProgress,
    retryExtraction,
    removeJob,
    documents,
    documentsLoading,
  } = useDocumentExtraction({
    clientId,
    orgId: orgId || undefined,
    familyId: familyId || undefined,
    maxConcurrent: 3,
    onExtractionComplete: (job) => {
      // Don't auto-navigate when batch uploading
      if (!batchProgress.inProgress) {
        navigate(`/data/review/${job.documentId}`);
      }
    },
  });

  // Handle batch upload
  const handleBatchUpload = useCallback(async (files: File[]) => {
    await uploadBatch(files);
    trackEvent('document_upload', { file_count: files.length });
  }, [uploadBatch]);

  const handleReview = (job: ExtractionJob) => {
    navigate(`/data/review/${job.documentId}`);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "uploaded":
        return <FileText className="h-4 w-4" />;
      case "processing":
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case "completed":
        return <CheckCircle2 className="h-4 w-4 text-success" />;
      case "failed":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getProgress = (status: string) => {
    switch (status) {
      case "uploaded":
        return 25;
      case "processing":
        return 60;
      case "completed":
        return 100;
      case "failed":
        return 100;
      default:
        return 0;
    }
  };

  return (
    <div className="space-y-6">
      {/* Batch Upload Zone */}
      <BatchUploadZone
        onUpload={handleBatchUpload}
        isUploading={batchProgress.inProgress}
        completedCount={batchProgress.completed}
        totalCount={batchProgress.total}
      />

      {/* Active Jobs */}
      {activeJobs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Extraction Queue</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeJobs.map((job) => (
              <div
                key={job.documentId}
                className="flex items-center gap-4 p-3 rounded-lg border bg-card"
              >
                {getStatusIcon(job.status)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{job.filename}</span>
                    <Badge variant={statusVariants[job.status] || "secondary"}>
                      {job.status}
                    </Badge>
                    {job.documentType && (
                      <Badge variant="outline">
                        {documentTypeLabels[job.documentType] || job.documentType}
                      </Badge>
                    )}
                  </div>
                  {job.status === "processing" && (
                    <div className="mt-2">
                      <Progress value={undefined} className="h-1" />
                      <p className="text-xs text-muted-foreground mt-1">
                        Extracting data... This usually takes 10-30 seconds
                      </p>
                    </div>
                  )}
                  {job.error && (
                    <p className="text-sm text-destructive mt-1">{job.error}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {job.status === "completed" && job.data && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleReview(job)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Review
                    </Button>
                  )}
                  {job.status === "failed" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => retryExtraction(job.documentId)}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Retry
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeJob(job.documentId)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Document Queue - Persistent list of all documents */}
      <DocumentQueue />

      {/* Info Card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <h3 className="font-semibold mb-4">How it works</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">1. Upload PDF</p>
                <p className="text-muted-foreground">
                  Upload capital calls, distribution notices, or quarterly statements
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">2. AI Classification</p>
                <p className="text-muted-foreground">
                  Documents are automatically classified by type
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">3. Data Extraction</p>
                <p className="text-muted-foreground">
                  Key financial data is extracted using Gemini AI
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">4. Review & Approve</p>
                <p className="text-muted-foreground">
                  Review extracted data before it's saved to your portfolio
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}

export default ExtractionUploadTab;
